import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { ENDPOINTS } from '../../../Utils/Endpoints';
import AlertDialog from '../../Shared/AlertDialog';

export default function PeticionesAtendidasModal({ open, cursoInfo, onClose, onSuccess }) {
    const [peticiones, setPeticiones] = useState([]);
    const [loading, setLoading] = useState(false);

    // Modal de alertas estándar LMS
    const [alertDialog, setAlertDialog] = useState({ open: false, title: "", message: "", variant: "info" });

    // Store modifications in local state indexed by ID
    const [modifiedDates, setModifiedDates] = useState({});
    const [modifiedStatus, setModifiedStatus] = useState({});

    useEffect(() => {
        if (open && cursoInfo?.idEstudianteCurso) {
            loadPeticiones(cursoInfo.idEstudianteCurso);
        } else {
            setPeticiones([]);
            setModifiedDates({});
            setModifiedStatus({});
        }
    }, [open, cursoInfo]);

    const loadPeticiones = async (idEstudianteCurso) => {
        setLoading(true);
        try {
            const apiBase = ENDPOINTS.cursos ? ENDPOINTS.cursos.replace('/Curso', '') : 'http://localhost:5248/api';
            let url = ENDPOINTS.peticionFechaExtendida
                ? `${ENDPOINTS.peticionFechaExtendida}/EstudianteCurso/${idEstudianteCurso}`
                : `${apiBase}/PeticionFechaExtendida/EstudianteCurso/${idEstudianteCurso}`;

            const resPeticiones = await axios.get(url);
            const data = resPeticiones.data || [];

            // Only keeping those that are attended
            const atendidas = data.filter(p => (p.atendido ?? p.Atendido) === true);
            setPeticiones(atendidas);

            // Initialize local modifications with current fechaActual and status
            const initModsDates = {};
            const initModsStatus = {};
            atendidas.forEach(p => {
                const dateVal = p.fechaActual ?? p.FechaActual ? new Date(p.fechaActual ?? p.FechaActual) : null;
                const petId = p.idPeticionFechaExtendida ?? p.IdPeticionFechaExtendida;

                if (dateVal) {
                    const yyyy = dateVal.getFullYear();
                    const mm = String(dateVal.getMonth() + 1).padStart(2, '0');
                    const dd = String(dateVal.getDate()).padStart(2, '0');
                    initModsDates[petId] = `${yyyy}-${mm}-${dd}`;
                } else {
                    initModsDates[petId] = '';
                }
                initModsStatus[p.idPeticionFechaExtendida ?? p.IdPeticionFechaExtendida] = !!(p.aceptado ?? p.Aceptado);
            });
            setModifiedDates(initModsDates);
            setModifiedStatus(initModsStatus);

        } catch (e) {
            console.error("Error cargando peticiones atendidas:", e);
            setAlertDialog({
                open: true,
                title: 'Error de Carga',
                message: "No se pudieron cargar las peticiones.",
                variant: 'error'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleDateChange = (id, newDate) => {
        setModifiedDates(prev => ({ ...prev, [id]: newDate }));
    };

    const handleStatusChange = (id, newStatus) => {
        setModifiedStatus(prev => ({ ...prev, [id]: newStatus === 'true' }));
    };

    const isRowChanged = (pet) => {
        const fa = pet.fechaActual ?? pet.FechaActual;
        const originalDateVal = fa
            ? new Date(fa).toISOString().split('T')[0]
            : '';
        const curDate = modifiedDates[pet.idPeticionFechaExtendida ?? pet.IdPeticionFechaExtendida] || '';
        const curStatus = modifiedStatus[pet.idPeticionFechaExtendida ?? pet.IdPeticionFechaExtendida];

        return curDate !== originalDateVal || curStatus !== !!(pet.aceptado ?? pet.Aceptado);
    };

    const handleUpdate = async (peticion) => {
        const id = peticion.idPeticionFechaExtendida ?? peticion.IdPeticionFechaExtendida;
        const nuevaFechaStr = modifiedDates[id];
        const nuevoStatus = modifiedStatus[id];

        if (nuevoStatus && !nuevaFechaStr) {
            setAlertDialog({
                open: true,
                title: 'Validación',
                message: "Por favor, seleccione una fecha válida para la petición aprobada.",
                variant: 'warning'
            });
            return;
        }

        try {
            setLoading(true);
            const apiBase = ENDPOINTS.cursos ? ENDPOINTS.cursos.replace('/Curso', '') : 'http://localhost:5248/api';
            const urlPet = ENDPOINTS.peticionFechaExtendida || `${apiBase}/PeticionFechaExtendida`;

            // 1. Update the petition row
            const peticionActualizada = {
                ...peticion,
                Aceptado: nuevoStatus, // Para asegurar que ambos nombres esten correctos si se envia al back
                aceptado: nuevoStatus,
                fechaActual: nuevoStatus && nuevaFechaStr ? new Date(nuevaFechaStr).toISOString() : (peticion.fechaActual ?? peticion.FechaActual),
                FechaActual: nuevoStatus && nuevaFechaStr ? new Date(nuevaFechaStr).toISOString() : (peticion.fechaActual ?? peticion.FechaActual)
            };

            await axios.put(urlPet, peticionActualizada);

            // 2. Update EstudianteCurso to sync the limit date
            const urlEstCur = ENDPOINTS.estudiantesCurso || `${apiBase}/EstudianteCurso`;
            const resEC = await axios.get(`${urlEstCur}/${cursoInfo.idEstudianteCurso}`);

            if (resEC.data) {
                // If rejected, maybe revert to fechaAnterior. If approved, apply new date.
                const fa = peticion.fechaAnterior ?? peticion.FechaAnterior;
                const fallbackDate = fa ? new Date(fa).toISOString() : (resEC.data.fechaTermino ?? resEC.data.FechaTermino);
                const ecUpdated = {
                    ...resEC.data,
                    fechaTermino: nuevoStatus ? new Date(nuevaFechaStr).toISOString() : fallbackDate,
                    FechaTermino: nuevoStatus ? new Date(nuevaFechaStr).toISOString() : fallbackDate
                };
                await axios.put(urlEstCur, ecUpdated);
            }

            if (onSuccess) onSuccess("Petición actualizada correctamente.", "success", "Éxito");
            else {
                setAlertDialog({
                    open: true,
                    title: 'Éxito',
                    message: "Petición actualizada correctamente.",
                    variant: 'success'
                });
            }

            // Reload the list
            await loadPeticiones(cursoInfo.idEstudianteCurso);
            // Si llamamos onSuccess aquí, cerrará el modal porque handleSuccessPeticion llama a onClose
            // Para mantener el modal abierto, sólo actualizamos la info silenciosamente y
            // quizás notificamos al padre de otra manera si realmente lo requiere,
            // pero si la petición es atendida, el botón "Ver Petición" igual va a quedarse bloqueado
            // así que no afecta omitir el refresco agresivo hasta que el usuario cierre.

        } catch (e) {
            console.error("Error actualizando petición:", e);
            setAlertDialog({
                open: true,
                title: 'Error',
                message: "Ocurrió un error al actualizar la petición.",
                variant: 'error'
            });
        } finally {
            setLoading(false);
        }
    };

    if (!open) return null;

    return (
        <div
            className="roles-confirm-overlay"
            role="dialog"
            aria-modal="true"
            style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(15,23,42,.45)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 16,
                zIndex: 2000,
            }}
            onClick={(e) => {
                if (e.target === e.currentTarget) {
                    if (onSuccess) onSuccess();
                    onClose?.();
                }
            }}
        >
            <div
                className="roles-confirm-card"
                style={{
                    width: 'min(750px, 96vw)',
                    background: '#fff',
                    borderRadius: 12,
                    boxShadow: '0 10px 30px rgba(0,0,0,.25)',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    maxHeight: '90vh'
                }}
            >
                {/* Header */}
                <div
                    className="roles-topbar"
                    style={{
                        marginBottom: 0,
                        padding: '1rem 1.5rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        borderBottom: '1px solid #e2e8f0'
                    }}
                >
                    <h3 className="roles-title" style={{ margin: 0 }}>
                        Peticiones de Extensión Atendidas
                    </h3>
                    <button className="roles-btn" onClick={onClose} aria-label="Cerrar" style={{ border: 'none', background: 'transparent', fontSize: '1.25rem', cursor: 'pointer' }}>✕</button>
                </div>

                {/* Body */}
                <div style={{ padding: '1.5rem', overflowY: 'auto', flexGrow: 1 }}>
                    {loading && peticiones.length === 0 ? (
                        <p style={{ textAlign: 'center' }}>Cargando peticiones...</p>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                            <div style={{ background: '#f8fafc', padding: '1rem', borderRadius: 8 }}>
                                <p style={{ margin: '0' }}><strong>Curso:</strong> {cursoInfo?.nombreCurso}</p>
                            </div>

                            {peticiones.length === 0 ? (
                                <p style={{ textAlign: 'center', color: '#64748b' }}>No hay peticiones atendidas para este curso.</p>
                            ) : (
                                <table className="roles-table" style={{ width: '100%', marginTop: '1rem' }}>
                                    <thead style={{ background: '#f1f5f9' }}>
                                        <tr>
                                            <th style={{ padding: '0.75rem', textAlign: 'center' }}>Estado</th>
                                            <th style={{ padding: '0.75rem', textAlign: 'center' }}>Fecha Original</th>
                                            <th style={{ padding: '0.75rem', textAlign: 'center' }}>Nueva Fecha / Actual</th>
                                            <th style={{ padding: '0.75rem', textAlign: 'center' }}>Acciones</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {peticiones.map(pet => {
                                            const rowChanged = isRowChanged(pet);
                                            const id = pet.idPeticionFechaExtendida ?? pet.IdPeticionFechaExtendida;
                                            const isApproved = modifiedStatus[id];

                                            return (
                                                <tr key={id}>
                                                    <td style={{ padding: '0.75rem', textAlign: 'center', verticalAlign: 'middle' }}>
                                                        <span style={{
                                                            fontSize: '0.85rem',
                                                            fontWeight: 600,
                                                            color: isApproved ? '#166534' : '#991b1b',
                                                            userSelect: 'none'
                                                        }}>
                                                            {isApproved ? 'Aprobada' : 'Rechazada'}
                                                        </span>
                                                    </td>
                                                    <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                                                        {(pet.fechaAnterior ?? pet.FechaAnterior) ? new Date(pet.fechaAnterior ?? pet.FechaAnterior).toLocaleDateString() : 'N/A'}
                                                    </td>
                                                    <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                                                        <input
                                                            type="date"
                                                            className="infoField"
                                                            value={modifiedDates[id] || ''}
                                                            onChange={(e) => handleDateChange(id, e.target.value)}
                                                            disabled={!isApproved}
                                                            style={{ opacity: isApproved ? 1 : 0.5 }}
                                                        />
                                                    </td>
                                                    <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                                                        <button
                                                            type="button"
                                                            className="btn-action"
                                                            style={{ padding: '0.4rem 0.8rem', fontSize: '0.85rem', opacity: rowChanged ? 1 : 0.5 }}
                                                            onClick={() => handleUpdate(pet)}
                                                            disabled={!rowChanged}
                                                        >
                                                            Actualizar
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div
                    style={{
                        padding: '1rem 1.5rem',
                        borderTop: '1px solid #e2e8f0',
                        display: 'flex',
                        justifyContent: 'flex-end',
                        background: '#f8fafc'
                    }}
                >
                    <button className="btn-cancel" onClick={() => {
                        if (onSuccess) onSuccess();
                        onClose?.();
                    }}>Cerrar</button>
                </div>
            </div>
            <AlertDialog
                open={alertDialog.open}
                title={alertDialog.title}
                message={alertDialog.message}
                variant={alertDialog.variant}
                onClose={() => setAlertDialog(prev => ({ ...prev, open: false }))}
            />
        </div>
    );
}
